import { Box, Typography } from '@mui/material';
import MessageGroup from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup';
import { HrBotContext } from 'components/@home/drawers/HrBotDrawer/hrBotContext';
import extractionFileMatchesForSend from 'components/@home/@documents/SentDocuments/extractionFileMatch';
import { getFileName } from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import IphonePreview from 'components/common/IphonePreview';
import LinearProgressWithLabel from 'components/controls/LinearProgressWithLabel';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import { func, object } from 'prop-types';
import React, { memo, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { HR_BOT_APP_ID } from 'services/constants';

const StepSend = ({ setError, setNextEnabled, userItems }) => {
  const bulkMessaging = useSelector(state => state.bulkMessaging.bulkMessaging);
  const app = useSelector(state => state.apps.apps[HR_BOT_APP_ID]);
  const { files, message, uploadedPct, format } = useContext(HrBotContext);
  const sendByField = format.sendBy || 'code';
  const file = useMemo(
    () => ({ ...files?.find(f => !f.error && extractionFileMatchesForSend(f, sendByField)) }),
    [files, sendByField],
  );

  const numSend = useMemo(
    () => files?.filter(f => !f.error && extractionFileMatchesForSend(f, sendByField)).length || 0,
    [files, sendByField],
  );
  const numNotFound = useMemo(
    () => files?.filter(f => !f.error && !extractionFileMatchesForSend(f, sendByField)).length || 0,
    [files, sendByField],
  );
  const name = useMemo(
    () => getFileName({ ...file, ...userItems }, format),
    [file, userItems, format],
  );

  useEffect(() => {
    setNextEnabled(!!message);
    setError(null);
  }, [message, setError, setNextEnabled]);

  return (
    <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column', pr: '40px' }}>
      <Box
        sx={{
          width: '100%',
          mt: 1,
          display: 'flex',
          justifyContent: 'space-around',
        }}
      >
        <Box sx={{ width: '50%' }}>
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              '& svg': {
                mr: 2,
              },
            }}
          >
            <Typography>{I18n.t('HrBot.will be sent', { count: numSend })}</Typography>
          </Box>
          {numNotFound > 0 && (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  mr: 2,
                },
              }}
            >
              <Typography>{I18n.t('HrBot.will not be sent', { count: numNotFound })}</Typography>
            </Box>
          )}
          <Box sx={{ mt: 5 }}>
            <Typography>{I18n.t('HrBot.Upload progress')}</Typography>
            <LinearProgressWithLabel value={uploadedPct} />
          </Box>
          <Box sx={{ mt: 5 }}>
            <Typography>{I18n.t('HrBot.Send progress')}</Typography>
            <LinearProgressWithLabel
              value={
                bulkMessaging
                  ? ((bulkMessaging.sent + bulkMessaging.error) / bulkMessaging.total) * 100
                  : 0
              }
            />
            {bulkMessaging?.error > 0 && (
              <Typography color="error">
                {I18n.t('HrBot.Error sending files', { count: bulkMessaging.error })}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              '& svg': {
                mr: 2,
              },
            }}
          >
            <Typography>
              {I18n.t(bulkMessaging ? 'HrBot.You can now close' : 'HrBot.Please do not close')}
            </Typography>
          </Box>
          {bulkMessaging?.status === 'FINISHED' && (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  mr: 2,
                },
              }}
            >
              <Box sx={muiTheme => ({ color: muiTheme.palette.ok, display: 'inline-flex' })}>
                <CheckCircleIcon />
              </Box>
              <Typography>{I18n.t('HrBot.Finished', { count: bulkMessaging.sent })}</Typography>
            </Box>
          )}
        </Box>
        <IphonePreview>
          <Box
            sx={muiTheme => ({
              zoom: 0.73,
              flexDirection: 'column',
              p: '10px',
              flexGrow: 1,
              backgroundColor: muiTheme.palette.secondary.ultraLight,
              display: 'flex',
              justifyContent: 'flex-end',
              fontSize: 14,
            })}
          >
            {file?.file && (
              <MessageGroup
                msgRef={() => {}}
                message={{
                  text: message,
                  attachments: [
                    {
                      name,
                      httpLink: name,
                      mimeType: 'application/pdf',
                      type: 'doc',
                      size: file.file.size,
                    },
                  ],
                  employee: {
                    avatar: app.avatar,
                    firstName: app.name,
                  },
                }}
              />
            )}
          </Box>
        </IphonePreview>
      </Box>
    </Box>
  );
};

StepSend.propTypes = {
  setError: func.isRequired,
  setNextEnabled: func.isRequired,
  userItems: object.isRequired,
};

export default memo(StepSend);
